import { Button, Group, Stack, Text } from '@mantine/core';
import { ArrowRight, ArrowsClockwise } from '@phosphor-icons/react';
import { useEffect, useState, type FC } from 'react';

import {
    getChannelHealth,
    type DistributionChannelHealth,
    type DistributionSeverity,
    type DistributionOverviewData,
    type DistributionPlanSummary,
} from '@api/distribution';
import SectionHeader from '@components/common/SectionHeader';
import StatCard from '@components/ui/StatCard';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { RateBasisControl } from '@components/ui/RateBasisControl';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useRatePolicy } from '@hooks/useRatePolicy';
import { neutralColors, semanticColors } from '@styles/design-system';

import styles from './DistributionOverview.module.css';
import { formatCount } from '@utils/currency';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<DistributionSeverity, string> = {
    HIGH: semanticColors.critical.primary,
    MEDIUM: semanticColors.warning.primary,
    LOW: semanticColors.positive.primary,
};

const PLAN_STATUS_COLOR: Record<string, string> = {
    DRAFT: neutralColors.textMuted,
    APPROVED: semanticColors.info.primary,
    EXECUTING: semanticColors.info.primary,
    COMPLETED: semanticColors.positive.primary,
    ARCHIVED: neutralColors.textDisabled,
};

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

function formatDrr(drr: number): string {
    if (drr === 0) return '—';
    if (drr < 1) return drr.toFixed(2);
    return drr.toFixed(1);
}

// ─── Channel table columns ─────────────────────────────────────────────────────

function buildColumns(
    onChannelClick: (ch: DistributionChannelHealth) => void,
    rateLabel: string,
): DataSheetColumn<DistributionChannelHealth>[] {
    return [
        {
            key: 'channelName',
            header: 'Channel',
            width: 240,
            align: 'left',
            render: (_val, row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onChannelClick(row); }}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            color: semanticColors.info.primary,
                            textAlign: 'left',
                        }}
                    >
                        {row.channelName}
                    </button>
                    <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: row.isAggregated ? '#6366f1' : neutralColors.textSecondary,
                        backgroundColor: row.isAggregated ? 'rgba(99,102,241,0.08)' : 'rgba(0,0,0,0.05)',
                        borderRadius: 4,
                        padding: '2px 6px',
                        flexShrink: 0,
                    }}>
                        {row.isAggregated ? 'Aggregate' : 'FC'}
                    </span>
                </div>
            ),
        },
        {
            key: 'skuCount',
            header: 'SKUs below target',
            width: 140,
            align: 'right',
            render: (_val, row) => {
                const hasBelow = row.skuCount > 0;
                // Only colour HIGH red — MEDIUM stays neutral, LOW stays neutral
                const color = hasBelow && row.severity === 'HIGH'
                    ? semanticColors.critical.primary
                    : hasBelow
                    ? neutralColors.textPrimary
                    : neutralColors.textMuted;
                return (
                    <Text style={{ fontSize: 13, fontWeight: hasBelow ? 600 : 400, color }}>
                        {row.skuCount} of {row.totalSkuCount ?? '—'}
                    </Text>
                );
            },
        },
        {
            key: 'weightedDohDays',
            header: 'Avg DoH',
            width: 160,
            align: 'right',
            render: (_val, row) => {
                const doh = row.weightedDohDays;
                const target = row.targetDohDays;
                const pct = target > 0 ? Math.min(doh / target, 1) : 0;
                const color = doh < target * 0.5
                    ? semanticColors.critical.primary
                    : doh < target
                    ? semanticColors.warning.primary
                    : semanticColors.positive.primary;
                return (
                    <div style={{ textAlign: 'right' }}>
                        <Text style={{ fontSize: 13, fontWeight: 600, color }}>
                            {doh >= 999 ? '—' : `${Math.floor(doh)}d`}
                            <Text span style={{ fontSize: 11, fontWeight: 400, color: neutralColors.textMuted, marginLeft: 4 }}>
                                / {target}d
                            </Text>
                        </Text>
                        <div style={{
                            marginTop: 3, height: 3, borderRadius: 2,
                            backgroundColor: neutralColors.surfaceHover, overflow: 'hidden',
                        }}>
                            <div style={{ width: `${pct * 100}%`, height: '100%', backgroundColor: color, borderRadius: 2 }} />
                        </div>
                    </div>
                );
            },
        },
        {
            // The rate Avg DoH actually divides by, so the two columns reconcile on screen.
            key: 'channelRate',
            header: rateLabel,
            width: 96,
            align: 'right',
            render: (_val, row) => (
                <Text style={{ fontSize: 13, color: neutralColors.textSecondary }}>
                    {formatDrr(row.channelRate ?? row.channelDrr)}/d
                </Text>
            ),
        },
        {
            key: 'severity',
            header: 'Severity',
            width: 110,
            align: 'center',
            render: (_val, row) => {
                const color = SEVERITY_COLOR[row.severity];
                return (
                    <Group gap={6} justify="center" wrap="nowrap">
                        <span style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: color,
                            flexShrink: 0,
                        }} />
                        <Text style={{ fontSize: 12, color, fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {row.severity}
                        </Text>
                    </Group>
                );
            },
        },
    ];
}

// ─── Recent plans ──────────────────────────────────────────────────────────────

interface RecentPlansProps {
    plans: DistributionPlanSummary[];
    loading: boolean;
    onViewPlan: (plan: DistributionPlanSummary) => void;
}

const RecentPlans: FC<RecentPlansProps> = ({ plans, loading, onViewPlan }) => {
    if (loading) {
        return (
            <Stack gap={8}>
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} style={{ height: 60, borderRadius: 12, backgroundColor: 'rgba(0,0,0,0.04)' }} />
                ))}
            </Stack>
        );
    }

    if (plans.length === 0) {
        return (
            <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                No plans yet. Compute your first distribution plan to get started.
            </Text>
        );
    }

    return (
        <Stack gap={8}>
            {plans.map(plan => (
                <div key={plan.id} className={styles.planCard} onClick={() => onViewPlan(plan)}>
                    <div style={{ minWidth: 0 }}>
                        <Text style={{ fontSize: 14, fontWeight: 600, color: neutralColors.textPrimary }}>
                            {plan.name}
                        </Text>
                        <Text style={{ fontSize: 12, color: neutralColors.textMuted, marginTop: 2 }}>
                            {new Date(plan.planDate).toLocaleDateString()} ·{' '}
                            {plan.channelNames.join(', ')} ·{' '}
                            {plan.lineCount} line{plan.lineCount !== 1 ? 's' : ''}
                        </Text>
                    </div>
                    <Group gap={8} style={{ flexShrink: 0 }}>
                        <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: PLAN_STATUS_COLOR[plan.status] ?? neutralColors.textMuted,
                            backgroundColor: `${PLAN_STATUS_COLOR[plan.status]}18`,
                            borderRadius: 4,
                            padding: '3px 8px',
                        }}>
                            {plan.status}
                        </span>
                        <ArrowRight size={16} color={neutralColors.textMuted} />
                    </Group>
                </div>
            ))}
        </Stack>
    );
};

// ─── Overview root ─────────────────────────────────────────────────────────────

interface Props {
    onComputePlan: () => void;
    onViewPlan: (plan: DistributionPlanSummary) => void;
    onChannelClick: (channel: DistributionChannelHealth) => void;
    onShowAllPlans: () => void;
    draftPlans: DistributionPlanSummary[];
    draftPlansLoading: boolean;
    /** Channels in these abbreviation keys are PO-driven and shown in the PO Orders tab instead. */
    poChannelKeys?: Set<string>;
}

const DistributionOverview: FC<Props> = ({
    onComputePlan, onViewPlan, onChannelClick, onShowAllPlans,
    draftPlans, draftPlansLoading, poChannelKeys,
}) => {
    const [overview, setOverview] = useState<DistributionOverviewData | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [overviewError, setOverviewError] = useState(false);

    const { currentUser } = useCurrentUser();
    const rate = useRatePolicy(currentUser?.organizationId);
    const { effectiveBasis, effectiveWindow, setSaved } = rate;

    const refresh = (bust = false) => {
        setOverviewLoading(true);
        setOverviewError(false);

        getChannelHealth({
            bust,
            basis: effectiveBasis,
            drrLookbackDays: effectiveBasis === 'BACKWARD' ? effectiveWindow : undefined,
        })
            .then(data => {
                setOverview(data);
                // Adopt the org's saved policy once it lands, so the control measures "dirty"
                // against the real default rather than the placeholder it starts with.
                if (data.savedRateBasis && data.savedDrrWindowDays != null) {
                    setSaved(data.savedRateBasis, data.savedDrrWindowDays);
                }
            })
            .catch(() => setOverviewError(true))
            .finally(() => setOverviewLoading(false));
    };

    // Refetches whenever the viewer changes basis or window — the rate is computed server-side.
    useEffect(() => {
        refresh(false);
    }, [effectiveBasis, effectiveWindow]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalGapUnits = overview?.totalGapUnits ?? 0;
    const totalGapSkus = overview?.totalGapSkus ?? 0;
    const channelsBelowTarget = overview?.channelsBelowTarget ?? 0;
    const totalChannels = overview?.totalChannels ?? 0;
    const internalAvailableUnits = overview?.internalAvailableUnits ?? 0;
    const channels = (overview?.channels ?? []).filter(
        c => !poChannelKeys?.has(c.channelAbbreviationKey),
    );

    // Under a forecast basis the number is not a run rate, so the column says so.
    const rateLabel = effectiveBasis === 'FORWARD' ? 'Forecast rate' : `DRR ${effectiveWindow}d`;
    const columns = buildColumns(onChannelClick, rateLabel);

    return (
        <Stack gap={24}>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <StatCard
                    label="Products under threshold"
                    value={overviewError ? '—' : String(totalGapSkus)}
                    helper="SKUs below target DoH across all channels"
                    valueColor={totalGapSkus > 0 ? semanticColors.critical.primary : undefined}
                    loading={overviewLoading}
                />
                <StatCard
                    label="Total SKU Gap"
                    value={overviewError ? '—' : formatUnits(totalGapUnits)}
                    helper={`units needed across ${totalGapSkus} below-target SKU${totalGapSkus !== 1 ? 's' : ''}`}
                    loading={overviewLoading}
                />
                <StatCard
                    label="Internal availability"
                    value={overviewError ? '—' : formatUnits(internalAvailableUnits)}
                    helper="units in internal warehouses"
                    loading={overviewLoading}
                />
            </div>

            {/* Channel health table */}
            <div>
                <SectionHeader
                    title="Channel Health"
                    actions={
                        <Group gap={8} wrap="nowrap">
                            <RateBasisControl
                                metricLabel="DRR"
                                savedBasis={rate.savedBasis}
                                effectiveBasis={rate.effectiveBasis}
                                effectiveWindow={rate.effectiveWindow}
                                isDirty={rate.isDirty}
                                saving={rate.saving}
                                onBasisChange={rate.setBasis}
                                onWindowChange={rate.setWindow}
                                onSave={rate.save}
                                onReset={rate.reset}
                            />
                            <Button
                                variant="subtle"
                                size="xs"
                                fw={500}
                                leftSection={<ArrowsClockwise size={14} />}
                                onClick={() => refresh(true)}
                                loading={overviewLoading}
                                color="dark"
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="action"
                                size="sm"
                                rightSection={<ArrowRight size={16} />}
                                onClick={onComputePlan}
                            >
                                Compute Plan
                            </Button>
                        </Group>
                    }
                />
                <DataSheet
                    columns={columns}
                    data={channels}
                    isLoading={overviewLoading}
                    hideHeaderBar
                    maxHeight="auto"
                    getRowKey={row => row.channelId}
                    onRowClick={row => onChannelClick(row)}
                />
            </div>

            {/* Continue working — DRAFT plans only */}
            {(draftPlansLoading || draftPlans.length > 0) && (
                <div>
                    <SectionHeader
                        title="Continue Working on These Plans"
                        actions={
                            <Button variant="subtle" size="xs" fw={500} rightSection={<ArrowRight size={14} />} onClick={onShowAllPlans} color="dark">
                                Show all plans
                            </Button>
                        }
                    />
                    <RecentPlans
                        plans={draftPlans}
                        loading={draftPlansLoading}
                        onViewPlan={onViewPlan}
                    />
                </div>
            )}

        </Stack>
    );
};

export default DistributionOverview;
